import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PaymentIcon from '@mui/icons-material/Payment';
import { useAuth } from '../../contexts/AuthContext';
import { mainApi } from '../../config/apiClient';
import { PAYMENT_SERVICE_URL } from '../../config/api';
import GuideButton from '../common/guide/GuideButton';

interface MokaCommissionRow {
  bank: string;
  group: string;
  creditType?: string | null;
  cashRate?: number | null;
  installmentRates?: Record<string, number | null>;
}

interface MokaCommissionTable {
  success: boolean;
  failureReason?: string | null;
  source?: string | null;
  rows?: MokaCommissionRow[];
}

const INSTALLMENT_COLUMNS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const formatRate = (rate?: number | null) =>
  rate == null || Number.isNaN(rate) ? '—' : `%${rate.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface EditPaymentIntegrationModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  integrationId: number | null;
}

const EditPaymentIntegrationModal: React.FC<EditPaymentIntegrationModalProps> = ({
  open,
  onClose,
  onSaved,
  integrationId,
}) => {
  const { branches, userDetails } = useAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [provider, setProvider] = useState<string>('paytr');
  const [branchId, setBranchId] = useState<string>('');
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [isTestMode, setIsTestMode] = useState<boolean>(false);
  const [is3DEnabled, setIs3DEnabled] = useState<boolean>(true);

  const [merchantId, setMerchantId] = useState<string>('');
  const [merchantKey, setMerchantKey] = useState<string>('');
  const [merchantSalt, setMerchantSalt] = useState<string>('');
  const [walletId, setWalletId] = useState<string>('-1');
  const [paymentBranchCode, setPaymentBranchCode] = useState<string>('0');
  const [showMerchantKey, setShowMerchantKey] = useState<boolean>(false);
  const [showMerchantSalt, setShowMerchantSalt] = useState<boolean>(false);
  // PayTR kayıtlı site adresi (SettingsJson.registeredSiteUrl). Doluysa ödeme bu
  // adresteki köprü sayfasından açılır; PayTR mağaza domain kilidi böyle aşılır.
  const [registeredSiteUrl, setRegisteredSiteUrl] = useState<string>('');

  // Moka alanları (ClientCode=DealerCode, ClientUsername=Username, ClientPassword=Password)
  const [mokaDealerCode, setMokaDealerCode] = useState<string>('');
  const [mokaUsername, setMokaUsername] = useState<string>('');
  const [mokaPassword, setMokaPassword] = useState<string>('');
  const [showMokaPassword, setShowMokaPassword] = useState<boolean>(false);
  const [commissionOpen, setCommissionOpen] = useState<boolean>(false);
  const [commissionLoading, setCommissionLoading] = useState<boolean>(false);
  const [commissionError, setCommissionError] = useState<string | null>(null);
  const [commission, setCommission] = useState<MokaCommissionTable | null>(null);

  const loadCommissions = async () => {
    const companyId = userDetails?.companyId;
    if (!companyId) {
      setCommissionError('Şirket bilgisi bulunamadı.');
      setCommissionOpen(true);
      return;
    }
    setCommissionOpen(true);
    setCommissionLoading(true);
    setCommissionError(null);
    setCommission(null);
    try {
      const resp = await fetch(`${PAYMENT_SERVICE_URL}/api/payments/moka/commissions`, {
        headers: { 'X-Company-Id': companyId },
      });
      const json = (await resp.json().catch(() => null)) as MokaCommissionTable | null;
      if (!resp.ok || !json?.success) {
        setCommissionError(json?.failureReason || (json as { message?: string } | null)?.message || 'Komisyon tablosu alınamadı.');
        return;
      }
      setCommission(json);
    } catch {
      setCommissionError('Moka komisyon servisine bağlanılamadı.');
    } finally {
      setCommissionLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !integrationId) return;

    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await mainApi.get(`/api/paymentproviderintegration/${integrationId}`);
        const result = response.data;
        if (result.Success === false || result.success === false) {
          throw new Error(result.Message || result.message || 'Kayıt yüklenemedi.');
        }
        const d = result.Data || result.data;
        setProvider(d.Provider ?? d.provider ?? 'paytr');
        setBranchId((d.BranchId ?? d.branchId) || '');
        setIsEnabled(!!(d.IsEnabled ?? d.isEnabled));
        setIsTestMode(!!(d.IsTestMode ?? d.isTestMode));
        setIs3DEnabled(!!(d.Is3DEnabled ?? d.is3DEnabled));
        setMerchantId((d.MerchantId ?? d.merchantId) || '');
        setMerchantKey((d.MerchantKey ?? d.merchantKey) || '');
        setMerchantSalt((d.MerchantSalt ?? d.merchantSalt) || '');
        setWalletId((d.ClientCode ?? d.clientCode) || '-1');
        setPaymentBranchCode((d.Guid ?? d.guid) || '0');
        setMokaDealerCode((d.ClientCode ?? d.clientCode) || '');
        setMokaUsername((d.ClientUsername ?? d.clientUsername) || '');
        setMokaPassword((d.ClientPassword ?? d.clientPassword) || '');
        setShowMokaPassword(false);
        setShowMerchantKey(false);
        setShowMerchantSalt(false);

        // PayTR kayıtlı site adresini SettingsJson'dan oku (varsa).
        const settingsRaw = (d.SettingsJson ?? d.settingsJson) as string | null | undefined;
        let regSite = '';
        if (settingsRaw) {
          try {
            regSite = (JSON.parse(settingsRaw)?.registeredSiteUrl as string) || '';
          } catch {
            regSite = '';
          }
        }
        setRegisteredSiteUrl(regSite);
      } catch (err: any) {
        setError(
          err?.response?.data?.Message ||
            err?.response?.data?.message ||
            err?.message ||
            'Kayıt yüklenirken bir hata oluştu.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [open, integrationId]);

  const handleSubmit = async () => {
    if (!integrationId) return;
    setError(null);

    if (provider === 'paytr') {
      if (!merchantId.trim()) {
        setError('Merchant ID zorunludur.');
        return;
      }
      if (!merchantKey.trim()) {
        setError('Merchant Key zorunludur.');
        return;
      }
      if (!merchantSalt.trim()) {
        setError('Merchant Salt zorunludur.');
        return;
      }
    }

    if (provider === 'tacreocard') {
      if (!merchantId.trim()) {
        setError('Affiliate Id zorunludur.');
        return;
      }
      if (!merchantKey.trim()) {
        setError('Hash zorunludur.');
        return;
      }
    }

    if (provider === 'moka') {
      if (!mokaDealerCode.trim()) {
        setError('DealerCode zorunludur.');
        return;
      }
      if (!mokaUsername.trim()) {
        setError('Username zorunludur.');
        return;
      }
      if (!mokaPassword.trim()) {
        setError('Password zorunludur.');
        return;
      }
    }

    setSaving(true);
    try {
      const isTacreo = provider === 'tacreocard';
      const isMoka = provider === 'moka';
      const isPaytr = provider === 'paytr';
      const payload = {
        Id: integrationId,
        BranchId: branchId.trim() || null,
        IsEnabled: isEnabled,
        IsTestMode: isTestMode,
        Is3DEnabled: isTacreo ? false : is3DEnabled,
        MerchantId: isMoka ? null : (merchantId.trim() || null),
        MerchantKey: isMoka ? null : (merchantKey.trim() || null),
        MerchantSalt: isTacreo
          ? (merchantSalt.trim() || 'https://services.tacreocard.com')
          : isMoka ? null : merchantSalt.trim() || null,
        ClientCode: isTacreo ? (walletId.trim() || '-1') : isMoka ? mokaDealerCode.trim() : null,
        ClientUsername: isMoka ? mokaUsername.trim() : null,
        ClientPassword: isMoka ? mokaPassword.trim() : null,
        Guid: isTacreo ? (paymentBranchCode.trim() || '0') : null,
        // SettingsJson yalnızca PayTR için yönetilir (kayıtlı site köprüsü).
        // Diğer sağlayıcılarda gönderilmez (undefined) => backend mevcut ayarı KORUR,
        // böylece Moka'nın taksit/komisyon ayarları ezilmez.
        SettingsJson: isPaytr
          ? (registeredSiteUrl.trim()
              ? JSON.stringify({ registeredSiteUrl: registeredSiteUrl.trim() })
              : '')
          : undefined,
      };

      const response = await mainApi.post('/api/paymentproviderintegration/update', payload);
      const result = response.data;

      if (result.Success === false || result.success === false) {
        throw new Error(result.Message || result.message || 'Kayıt güncellenemedi.');
      }

      onSaved();
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.Message ||
        err?.response?.data?.message ||
        err?.message ||
        'Kayıt güncellenirken bir hata oluştu.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Ödeme Entegrasyonu Düzenle
          </Typography>
          <Chip label={provider.toUpperCase()} size="small" color="primary" />
          <GuideButton guideKey="integrations.editPayment" size="small" />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="edit-branch-select-label">Şube (boş bırakılırsa şirket geneli)</InputLabel>
              <Select
                labelId="edit-branch-select-label"
                value={branchId}
                label="Şube (boş bırakılırsa şirket geneli)"
                onChange={(e) => setBranchId(e.target.value as string)}
              >
                <MenuItem value="">
                  <em>Şirket Geneli (tüm şubeler)</em>
                </MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 1 }}>
              <Chip label="Durum" size="small" />
            </Divider>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isEnabled}
                    onChange={(e) => setIsEnabled(e.target.checked)}
                    color="success"
                  />
                }
                label="Aktif"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isTestMode}
                    onChange={(e) => setIsTestMode(e.target.checked)}
                    color="warning"
                  />
                }
                label="Test Modu"
              />
              {provider === 'moka' && isTestMode && (
                <Typography variant="caption" color="warning.main" sx={{ alignSelf: 'center' }}>
                  Test bilgisi kullanın. Canlı DealerCode ile açık bırakılırsa Moka “bayi bulunamadı” döner.
                </Typography>
              )}
              {provider !== 'tacreocard' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={is3DEnabled}
                      onChange={(e) => setIs3DEnabled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="3D Secure"
                />
              )}
            </Box>

            {provider === 'paytr' && (
              <>
                <Divider sx={{ my: 1 }}>
                  <Chip label="PayTR Bilgileri" color="primary" size="small" />
                </Divider>

                <TextField
                  label="Merchant ID"
                  value={merchantId}
                  onChange={(e) => setMerchantId(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  autoComplete="off"
                />

                <TextField
                  label="Merchant Key"
                  value={merchantKey}
                  onChange={(e) => setMerchantKey(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  autoComplete="new-password"
                  type={showMerchantKey ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowMerchantKey((v) => !v)}
                          edge="end"
                        >
                          {showMerchantKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Merchant Salt"
                  value={merchantSalt}
                  onChange={(e) => setMerchantSalt(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  autoComplete="new-password"
                  type={showMerchantSalt ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowMerchantSalt((v) => !v)}
                          edge="end"
                        >
                          {showMerchantSalt ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Kayıtlı Site Adresi (opsiyonel)"
                  value={registeredSiteUrl}
                  onChange={(e) => setRegisteredSiteUrl(e.target.value.trim())}
                  fullWidth
                  size="small"
                  placeholder="https://www.ornek.com"
                  helperText="PayTR mağaza bilgileri yalnızca tek bir siteye tanımlıysa doldurun. Ödeme (örneğin e-posta linki) başka bir adreste başlarsa müşteri bu sitedeki güvenli ödeme sayfasına yönlendirilir, PayTR domain kilidine takılmaz. Ödeme zaten bu adreste açılıyorsa boş bırakın."
                />
              </>
            )}

            {provider === 'tacreocard' && (
              <>
                <Divider sx={{ my: 1 }}>
                  <Chip label="Tacreo Kart Bilgileri" color="warning" size="small" />
                </Divider>

                <TextField
                  label="Affiliate Id"
                  value={merchantId}
                  onChange={(e) => setMerchantId(e.target.value)}
                  fullWidth
                  size="small"
                  required
                />

                <TextField
                  label="Hash"
                  value={merchantKey}
                  onChange={(e) => setMerchantKey(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  type={showMerchantKey ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowMerchantKey((v) => !v)}
                          edge="end"
                        >
                          {showMerchantKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="API Base URL"
                  value={merchantSalt}
                  onChange={(e) => setMerchantSalt(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="https://services.tacreocard.com"
                />

                <FormControl fullWidth size="small">
                  <InputLabel id="edit-wallet-select-label">Cüzdan</InputLabel>
                  <Select
                    labelId="edit-wallet-select-label"
                    value={walletId}
                    label="Cüzdan"
                    onChange={(e) => setWalletId(e.target.value as string)}
                  >
                    <MenuItem value="-1">Tümü (-1)</MenuItem>
                    <MenuItem value="1">Giyim (1)</MenuItem>
                    <MenuItem value="0">Restoran (0)</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Branch Code"
                  value={paymentBranchCode}
                  onChange={(e) => setPaymentBranchCode(e.target.value)}
                  fullWidth
                  size="small"
                />
              </>
            )}

            {provider === 'moka' && (
              <>
                <Divider sx={{ my: 1 }}>
                  <Chip label="Moka United Bilgileri" color="primary" size="small" />
                </Divider>

                <TextField
                  label="DealerCode (Bayi Kodu)"
                  value={mokaDealerCode}
                  onChange={(e) => setMokaDealerCode(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  autoComplete="off"
                />

                <TextField
                  label="Username (API Kullanıcı Adı)"
                  value={mokaUsername}
                  onChange={(e) => setMokaUsername(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  autoComplete="off"
                />

                <TextField
                  label="Password (API Şifresi)"
                  value={mokaPassword}
                  onChange={(e) => setMokaPassword(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  autoComplete="new-password"
                  type={showMokaPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setShowMokaPassword((v) => !v)}
                          edge="end"
                        >
                          {showMokaPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button variant="outlined" onClick={() => { void loadCommissions(); }}>
                  Banka taksit oranlarını getir
                </Button>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={saving || loading}
          startIcon={saving ? <CircularProgress size={18} /> : undefined}
        >
          {saving ? 'Kaydediliyor...' : 'Güncelle'}
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog open={commissionOpen} onClose={() => setCommissionOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>Banka taksit komisyon oranları</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Oranlar Moka bayi hesabınıza tanımlıdır ve karttan sizin adınıza kesilen komisyondur.
          Müşterinin ödeme ekranındaki taksit tablosu şu an peşin tutarı böler; bu yüzdeler oraya eklenmez.
          Boş hücre, o taksitin bu kart programında kapalı olduğu anlamına gelir.
        </Typography>
        {commissionLoading && <CircularProgress size={28} />}
        {commissionError && <Alert severity="warning">{commissionError}</Alert>}
        {commission?.rows && commission.rows.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Banka</TableCell>
                  <TableCell>Program</TableCell>
                  <TableCell align="right">Peşin</TableCell>
                  {INSTALLMENT_COLUMNS.map((n) => (
                    <TableCell key={n} align="right">{n}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {commission.rows.map((row, index) => (
                  <TableRow key={`${row.bank}-${row.group}-${index}`}>
                    <TableCell>{row.bank || '—'}</TableCell>
                    <TableCell>{row.group || row.creditType || '—'}</TableCell>
                    <TableCell align="right">{formatRate(row.cashRate)}</TableCell>
                    {INSTALLMENT_COLUMNS.map((n) => (
                      <TableCell key={n} align="right">
                        {formatRate(row.installmentRates?.[String(n)])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCommissionOpen(false)}>Kapat</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EditPaymentIntegrationModal;
